(() => {
  const ROOT_SELECTOR = '[data-lnp-bundle-upgrade]';
  const FORM_SELECTOR = '[data-lnp-bundle-form]';
  const SOURCE = 'lnp-bundle-upgrade';

  const analyticsPayload = (root, extra = {}) => ({
    surface: 'single_pdp',
    current_style: root.dataset.currentStyleName,
    product_id: numberValue(root.dataset.lnProductId) || undefined,
    product_handle: root.dataset.lnProductHandle,
    product_title: root.dataset.lnProductTitle,
    bundle_id: numberValue(root.dataset.lnBundleId) || undefined,
    bundle_handle: root.dataset.lnBundleHandle,
    bundle_title: root.dataset.bundleTitle,
    variant_id: numberValue(root.dataset.bundleVariantId) || undefined,
    value: Number(root.dataset.lnValue) || undefined,
    currency: root.dataset.lnCurrency,
    pairing: root.dataset.lnPairing,
    ...extra,
  });

  const trackAnalytics = (name, root, extra = {}) => {
    try {
      window.LashNovaAnalytics?.track?.(name, analyticsPayload(root, extra));
    } catch (_error) {
      // Analytics must never interrupt the upgrade transaction.
    }
  };

  const analyticsErrorCode = (error) => {
    try {
      return window.LashNovaAnalytics?.classifyError?.(error) || 'cart_update_failed';
    } catch (_analyticsError) {
      return 'cart_update_failed';
    }
  };

  const numberValue = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  };

  const isNestedLine = (item) => {
    const relationship = item?.parent_relationship;
    return Boolean(relationship?.parent_key || relationship?.parent);
  };

  const standaloneItemsForVariant = (cart, variantId) =>
    (cart?.items || []).filter(
      (item) => numberValue(item.variant_id) === variantId && !isNestedLine(item)
    );

  const standaloneItemForVariant = (cart, variantId) => standaloneItemsForVariant(cart, variantId)[0] || null;

  const messageFromResponse = (data, fallback) => {
    const message = data?.description || data?.message || data?.errors;
    return typeof message === 'string' && message ? message : fallback;
  };

  const requestJson = async (url, options = {}) => {
    const response = await fetch(url, {
      credentials: 'same-origin',
      ...options,
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        ...(options.headers || {}),
      },
    });

    let data;
    try {
      data = await response.json();
    } catch (_error) {
      throw new Error('The cart could not be updated. Please try again.');
    }

    if (!response.ok || data?.status || data?.errors) {
      throw new Error(messageFromResponse(data, 'The cart could not be updated. Please try again.'));
    }

    return data;
  };

  const fetchCart = () => requestJson(`${window.routes.cart_url}.js`);

  const getCartSurface = () =>
    document.querySelector('cart-notification') || document.querySelector('cart-drawer');

  const getSections = (cartSurface) => {
    if (!cartSurface?.getSectionsToRender) return ['cart-drawer', 'cart-icon-bubble'];
    return cartSurface
      .getSectionsToRender()
      .map((section) => section.id)
      .filter(Boolean)
      .slice(0, 5);
  };

  const cartRequestBody = (payload, sections) => ({
    ...payload,
    sections,
    sections_url: window.location.pathname,
  });

  const addBundle = (variantId, sections) =>
    requestJson(window.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(
        cartRequestBody(
          {
            items: [{ id: variantId, quantity: 1 }],
          },
          sections
        )
      ),
    });

  const changeLine = (key, quantity, sections) =>
    requestJson(window.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(cartRequestBody({ id: key, quantity }, sections)),
    });

  const publishAndRender = async (cartData, trigger, notificationOptions = {}) => {
    const cartSurface = getCartSurface();

    if (!cartSurface) {
      window.location.assign(window.routes.cart_url);
      return;
    }

    cartSurface.setActiveElement?.(trigger);
    cartSurface.classList.remove('is-empty');

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      try {
        await publish(PUB_SUB_EVENTS.cartUpdate, {
          source: SOURCE,
          cartData,
        });
      } catch (subscriberError) {
        // The cart response is authoritative. Optional subscribers (quantity
        // rules, recommendations, analytics) must not suppress the drawer.
        console.error('Cart update subscriber failed:', subscriberError);
      }
    }

    try {
      cartSurface.renderContents(cartData, notificationOptions);
    } catch (renderError) {
      console.error('Cart drawer rendering failed:', renderError);
      window.location.assign(window.routes.cart_url);
    }
  };

  const getAddedLine = (addResponse) => addResponse?.items?.[0] || addResponse;

  const rollbackAddedBundle = async (addResponse, bundleVariantId, sections) => {
    const currentCart = await fetchCart();
    const addedKey = getAddedLine(addResponse)?.key;
    const addedLine =
      currentCart.items.find((item) => addedKey && item.key === addedKey) ||
      standaloneItemForVariant(currentCart, bundleVariantId);

    if (!addedLine) throw new Error('The added Bundle line could not be restored.');
    return changeLine(addedLine.key, Math.max(0, addedLine.quantity - 1), sections);
  };

  const createController = (root) => {
    if (root.dataset.lnpBundleReady === 'true') return;
    root.dataset.lnpBundleReady = 'true';

    const form = root.querySelector(FORM_SELECTOR);
    const submitButton = root.querySelector('[data-lnp-bundle-submit]');
    const actionLabel = root.querySelector('[data-lnp-bundle-action-label]');
    const upgradeNote = root.querySelector('[data-lnp-bundle-upgrade-note]');
    const errorWrapper = root.querySelector('[data-lnp-bundle-error]');
    const errorMessage = errorWrapper?.querySelector('.product-form__error-message');
    const liveStatus = root.querySelector('[data-lnp-bundle-live-status]');
    const actions = root.querySelector('.lnp-bundle-upgrade__actions');

    if (!form || !submitButton || !actionLabel) return;

    let busy = false;
    let syncRequest = 0;

    const currentVariantId = () => {
      const productInfo = root.closest('product-info');
      const mainVariantInput = productInfo?.querySelector('.lnp-product-form input[name="id"]');
      return numberValue(mainVariantInput?.value || root.dataset.currentVariantId);
    };

    const bundleVariantId = () => numberValue(root.dataset.bundleVariantId);
    const bundleTitle = root.dataset.bundleTitle || 'Bundle';
    const currentStyleName = root.dataset.currentStyleName || 'this style';

    const setError = (message = '') => {
      if (!errorWrapper || !errorMessage) return;
      errorMessage.textContent = message;
      errorWrapper.toggleAttribute('hidden', !message);
    };

    const setLiveStatus = (message = '') => {
      if (liveStatus) liveStatus.textContent = message;
    };

    const setMode = (mode) => {
      const isUpgrade = mode === 'upgrade';
      root.dataset.lnpBundleMode = mode;
      actionLabel.textContent = `${isUpgrade ? 'Upgrade to' : 'Add'} ${bundleTitle}`;
      upgradeNote?.toggleAttribute('hidden', !isUpgrade);
      submitButton.setAttribute(
        'aria-label',
        `${isUpgrade ? `Upgrade ${currentStyleName} to` : 'Add'} ${bundleTitle}, ${root.dataset.bundlePrice}`
      );
    };

    const setBusy = (value) => {
      busy = value;
      submitButton.toggleAttribute('disabled', value);
      submitButton.setAttribute('aria-disabled', String(value));
      submitButton.classList.toggle('loading', value);
      actions?.classList.toggle('is-checking', value);
      submitButton.querySelector('.loading__spinner')?.classList.toggle('hidden', !value);
    };

    const syncMode = async () => {
      const requestId = ++syncRequest;
      try {
        const cart = await fetchCart();
        if (requestId !== syncRequest || !root.isConnected) return;
        setMode(standaloneItemForVariant(cart, currentVariantId()) ? 'upgrade' : 'add');
      } catch (_error) {
        if (requestId === syncRequest) setMode('add');
      }
    };

    root.lnpSyncBundleMode = syncMode;

    root.addEventListener(
      'submit',
      async (event) => {
        if (event.target !== form) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        if (busy) return;

        const intendedMode = root.dataset.lnpBundleMode === 'upgrade' ? 'upgrade' : 'add';
        trackAnalytics('ln_bundle_offer_click', root, { mode: intendedMode });

        const currentId = currentVariantId();
        const bundleId = bundleVariantId();
        if (!currentId || !bundleId) {
          trackAnalytics(
            intendedMode === 'upgrade' ? 'ln_bundle_upgrade_error' : 'ln_bundle_add_error',
            root,
            { mode: intendedMode, error_code: 'invalid_product_state', final_state: 'unchanged' }
          );
          setError('This set is not ready to add. Please view the set details.');
          return;
        }

        setError();
        setLiveStatus();
        setBusy(true);

        const cartSurface = getCartSurface();
        const sections = getSections(cartSurface);
        let preCart;
        let addResponse;
        let shouldUpgrade = false;
        let analyticsOutcomeTracked = false;

        const showBundle = (cartData, message) =>
          publishAndRender(cartData, submitButton, {
            itemKey: getAddedLine(addResponse)?.key,
            variantId: bundleId,
            message,
            success: message !== 'Please review your cart',
          });
        const showRestoredCart = (cartData) =>
          publishAndRender(cartData, submitButton, {
            itemKey: standaloneItemForVariant(preCart, currentId)?.key,
            variantId: currentId,
            message: 'Your original cart was kept',
            success: false,
          });

        const trackOutcome = (name, details = {}) => {
          if (analyticsOutcomeTracked) return;
          analyticsOutcomeTracked = true;
          trackAnalytics(name, root, details);
        };

        try {
          preCart = await fetchCart();
          const standaloneBeforeAdd = standaloneItemForVariant(preCart, currentId);
          shouldUpgrade = Boolean(standaloneBeforeAdd);

          addResponse = await addBundle(bundleId, sections);

          if (!shouldUpgrade) {
            await showBundle(addResponse);
            trackOutcome('ln_bundle_add_success', { mode: 'add', quantity: 1 });
            setLiveStatus(`${bundleTitle} was added to your cart.`);
            return;
          }

          const cartAfterAdd = await fetchCart();
          const standaloneAfterAdd = standaloneItemForVariant(cartAfterAdd, currentId);

          if (!standaloneAfterAdd) {
            await showBundle(addResponse);
            trackOutcome('ln_bundle_add_success', {
              mode: 'add',
              quantity: 1,
              cart_state: 'standalone_missing_after_add',
            });
            setLiveStatus(`${bundleTitle} was added to your cart.`);
            return;
          }

          try {
            const finalCart = await changeLine(
              standaloneAfterAdd.key,
              Math.max(0, standaloneAfterAdd.quantity - 1),
              sections
            );
            await showBundle(finalCart, 'Your cart was upgraded');
            trackOutcome('ln_bundle_upgrade_success', {
              mode: 'upgrade',
              quantity: 1,
              replaced_quantity: 1,
              final_state: 'bundle_replaced_single',
            });
            setLiveStatus(`${currentStyleName} was replaced with ${bundleTitle}.`);
          } catch (_changeError) {
            try {
              const restoredCart = await rollbackAddedBundle(addResponse, bundleId, sections);
              if (restoredCart) await showRestoredCart(restoredCart);
              trackOutcome('ln_bundle_upgrade_error', {
                mode: 'upgrade',
                error_code: analyticsErrorCode(_changeError),
                rollback_status: 'restored',
                final_state: 'original_cart_kept',
              });
              setError(`We couldn't complete the upgrade. Your ${currentStyleName} was kept.`);
            } catch (_rollbackError) {
              await showBundle(addResponse, 'Please review your cart');
              trackOutcome('ln_bundle_upgrade_error', {
                mode: 'upgrade',
                error_code: analyticsErrorCode(_changeError),
                rollback_status: 'failed',
                final_state: 'bundle_and_single_present',
              });
              setError(
                `${bundleTitle} was added, but ${currentStyleName} could not be removed. Please review your cart.`
              );
            }
          }
        } catch (error) {
          if (addResponse && shouldUpgrade) {
            try {
              const restoredCart = await rollbackAddedBundle(addResponse, bundleId, sections);
              if (restoredCart) await showRestoredCart(restoredCart);
              trackOutcome('ln_bundle_upgrade_error', {
                mode: 'upgrade',
                error_code: analyticsErrorCode(error),
                rollback_status: 'restored',
                final_state: 'original_cart_kept',
              });
              setError(`We couldn't complete the upgrade. Your ${currentStyleName} was kept.`);
            } catch (_rollbackError) {
              try {
                await showBundle(addResponse, 'Please review your cart');
              } catch (_renderError) {
                // The server-side cart is still authoritative even if the drawer cannot repaint.
              }
              trackOutcome('ln_bundle_upgrade_error', {
                mode: 'upgrade',
                error_code: analyticsErrorCode(error),
                rollback_status: 'failed',
                final_state: 'bundle_and_single_present',
              });
              setError(
                `${bundleTitle} was added, but ${currentStyleName} could not be removed. Please review your cart.`
              );
            }
          } else if (addResponse) {
            try {
              await showBundle(addResponse);
            } catch (_renderError) {
              // The add succeeded; the customer can still open the cart from the header.
            }
            trackOutcome('ln_bundle_add_success', {
              mode: 'add',
              quantity: 1,
              cart_state: 'added_render_fallback',
            });
            setLiveStatus(`${bundleTitle} was added to your cart.`);
          } else {
            trackOutcome(
              intendedMode === 'upgrade' ? 'ln_bundle_upgrade_error' : 'ln_bundle_add_error',
              {
                mode: intendedMode,
                error_code: analyticsErrorCode(error),
                final_state: 'unchanged',
              }
            );
            setError(error?.message || 'The cart could not be updated. Please try again.');
          }
        } finally {
          setBusy(false);
          syncMode();
        }
      },
      true
    );

    syncMode();
  };

  const boot = () => document.querySelectorAll(ROOT_SELECTOR).forEach(createController);
  const syncAll = () =>
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => root.lnpSyncBundleMode?.());

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
  window.addEventListener('pageshow', syncAll);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source !== SOURCE) syncAll();
    });
  }
})();
